/**
 * App.tsx
 *
 * モバイルドロワー修正ポイント:
 *   - MobileDrawer は <Sidebar> を流用しない
 *     （.sidebar { display:none } が当たるため）
 *   - 専用クラス .mobile-overlay / .mobile-drawer を使用
 *   - z-index: overlay=1000 / drawer=1001
 *
 * モバイルヘッダー固定:
 *   - .topbar を CSS で position:fixed にする（index.css 参照）
 *   - .page-content に padding-top:66px を CSS で付与（index.css 参照）
 *   - App.tsx 側の追加対応は不要
 */

import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, NavLink, useLocation, Link, useNavigate } from 'react-router-dom'
import { AuthProvider, AuthGuard, useAuth } from './components/AuthGuard'
import { useIsMobile } from './hooks/useIsMobile'
import Login from './pages/Login'
import RunningCharacters from './components/RunningCharacters'
import Dashboard     from './pages/Dashboard'
import Works         from './pages/Works'
import Expenses      from './pages/Expenses'
import Personnel     from './pages/Personnel'
import Finance       from './pages/Finance'
import MonthlyReport from './pages/MonthlyReport'
import Reports       from './pages/Reports'

import AssistantReports    from './pages/AssistantReports'
import AssistantWorks      from './pages/AssistantWorks'
import AssistantAttendance from './pages/AssistantAttendance'
import AssistantShifts     from './pages/AssistantShifts'
import AssistantShiftRequest from './pages/AssistantShiftRequest'
import AssistantPayrollClaim from './pages/AssistantPayrollClaim'
import AssistantPayslips    from './pages/AssistantPayslips'
import AssistantHelp        from './pages/AssistantHelp'
import AdminHelp            from './pages/AdminHelp'
import HelpStaff            from './pages/HelpStaff'
import HelpSubmit           from './pages/HelpSubmit'
import AccountSettings     from './pages/AccountSettings'
import LoginSecurity       from './pages/LoginSecurity'
import AuthManagement      from './pages/AuthManagement'
import ThemeToggleButton from './components/ThemeToggleButton'
import ColorModeToggleButton from './components/ColorModeToggleButton'
import RunnerToggleButton from './components/RunnerToggleButton'
import MangaDashboard from './pages/MangaDashboard'
import Payroll from './pages/Payroll'
import FixedExpenseTemplates from './pages/FixedExpenseTemplates'
import ProductionPlanningSettings from './pages/ProductionPlanningSettings'
import ProductionPlan             from './pages/ProductionPlan'
import { useReportReminder } from './hooks/useReportReminder'
import { TempEmailBanner } from './components/TempEmailBanner'
import { MfaNudgeBanner } from './components/mfa/MfaNudgeBanner'
import { confirmLeave } from './utils/navigationGuard'
import {
  LayoutDashboard,
  BookOpen,
  Users,
  CreditCard,
  PiggyBank,
  FileText,
  Clock3,
  CalendarDays,
  CalendarCheck,
  WalletCards,
  Banknote,
  HelpCircle,
  UserPlus,
  ClipboardList,
} from 'lucide-react'

const NAV_ICON_SIZE = 18
const NAV_ICON_STROKE = 1.8

const ADMIN_NAV = [
  {
    group: 'メイン',
    items: [
      { to: '/', icon: <LayoutDashboard size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: 'ダッシュボード' },
    ],
  },
  {
    group: '制作管理',
    items: [
      { to: '/works', icon: <BookOpen size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '作品・話数' },
    ],
  },
  {
    group: '人員管理',
    items: [
      { to: '/personnel',  icon: <Users         size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: 'アシスタント' },
      { to: '/help-staff', icon: <UserPlus      size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: 'ヘルプスタッフ' },
      { to: '/reports',    icon: <ClipboardList size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '日報' },
    ],
  },
  {
    group: '財務管理',
    items: [
      { to: '/expenses',                icon: <CreditCard size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '支払管理' },
      { to: '/finance',                 icon: <PiggyBank  size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '収入・税積立' },
      { to: '/payroll-mgmt',           icon: <Banknote   size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '給与管理' },
      { to: '/monthly-report',         icon: <FileText   size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '月次レポート' },
    ],
  },
]

const ASSISTANT_NAV = [
  {
    group: '日報',
    items: [
      { to: '/reports', icon: <FileText size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '日報' },
    ],
  },
  {
    group: '勤怠',
    items: [
      { to: '/attendance', icon: <Clock3 size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '勤怠入力' },
    ],
  },
  {
    group: 'シフト',
    items: [
      { to: '/shift-request', icon: <CalendarCheck size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: 'シフト希望' },
      { to: '/shifts',        icon: <CalendarDays  size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '確定シフト' },
    ],
  },
  {
    group: '給与申告',
    items: [
      { to: '/payroll', icon: <WalletCards size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '給与申告' },
    ],
  },
  {
    group: '給与明細',
    items: [
      { to: '/payslips', icon: <Banknote size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '給与明細' },
    ],
  },
  {
    group: '作品・話数',
    items: [
      { to: '/works', icon: <BookOpen size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />, label: '作品・話数' },
    ],
  },
]

// ── ログアウト確認ダイアログ ─────────────────────────────────────
function LogoutConfirm({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px',
      }}
      onClick={onCancel}
    >
      <div style={{
        background: 'var(--paper)', borderRadius: 12,
        padding: '24px 20px', width: '100%', maxWidth: 320,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>ログアウト</div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 24 }}>ログアウトしますか？</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" style={{ flex: 1, height: 46 }} onClick={onCancel}>キャンセル</button>
          <button className="btn btn-primary"
            style={{ flex: 1, height: 46, background: 'var(--red)', borderColor: 'var(--red)' }}
            onClick={onConfirm}>
            ログアウト
          </button>
        </div>
      </div>
    </div>
  )
}

// ── PC サイドバー（モバイルでは CSS で display:none） ────────────
function Sidebar() {
  const { isAdmin, user } = useAuth()
  const nav = isAdmin ? ADMIN_NAV : ASSISTANT_NAV
  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}>
          <div className="sidebar-logo-name">MangaOS</div>
        </Link>
        <div className="sidebar-logo-sub">
          {isAdmin ? 'Studio ERP' : (user?.assistant_name ?? 'アシスタント')}
        </div>
      </div>
      <nav className="sidebar-nav">
        {nav.map((group) => (
          <div key={group.group}>
            <div className="nav-group-label">{group.group}</div>
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}
                onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      {/* ヘルプリンク：フッター直上に固定（権限別） */}
      <div className="sidebar-help-link">
        {isAdmin ? (
          <NavLink to="/admin-help"
            onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}
            className={({ isActive }) => `nav-item sidebar-help-nav-item${isActive ? ' active' : ''}`}>
            <span className="nav-icon" aria-hidden="true">
              <HelpCircle size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
            </span>
            管理者ヘルプ
          </NavLink>
        ) : (
          <NavLink to="/help"
            onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}
            className={({ isActive }) => `nav-item sidebar-help-nav-item${isActive ? ' active' : ''}`}>
            <span className="nav-icon" aria-hidden="true">
              <HelpCircle size={NAV_ICON_SIZE} strokeWidth={NAV_ICON_STROKE} />
            </span>
            使い方・ヘルプ
          </NavLink>
        )}
      </div>
      <div className="sidebar-footer">
        {isAdmin ? '制作/人員/財務' : 'アシスタントポータル'}
      </div>
    </aside>
  )
}

// ── モバイルドロワー ─────────────────────────────────────────────
// ★ .sidebar と完全独立。専用クラス .mobile-overlay / .mobile-drawer を使用。
// ★ .sidebar の CSS（display:none）の影響を受けない。
function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { isAdmin, user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const nav = isAdmin ? ADMIN_NAV : ASSISTANT_NAV

  if (!open) return null

  const go = (to: string) => { if (!confirmLeave()) return; navigate(to); onClose() }

  return (
    <>
      {showLogoutConfirm && (
        <LogoutConfirm
          onConfirm={async () => { setShowLogoutConfirm(false); await logout() }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}

      {/* 背景: z-index 1000 */}
      <div className="mobile-overlay" onClick={onClose} />

      {/* ドロワー本体: z-index 1001（overlayより確実に前面） */}
      <aside className="mobile-drawer">
        {/* ロゴ + 閉じるボタン */}
        <div className="mobile-drawer-logo">
          <div>
            <button onClick={() => go('/')}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
              <div className="mobile-drawer-logo-name">MangaOS</div>
            </button>
            <div className="mobile-drawer-logo-sub">
              {isAdmin ? 'Studio ERP' : 'アシスタントポータル'}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.5)', fontSize: 20,
            cursor: 'pointer', padding: '0 4px', lineHeight: 1,
          }} aria-label="閉じる">✕</button>
        </div>

        {/* ユーザー情報 */}
        <div className="mobile-drawer-user">
          {user?.assistant_name && (
            <div className="mobile-drawer-user-name">{user.assistant_name}</div>
          )}
          <div className="mobile-drawer-user-email">{user?.email ?? ''}</div>
        </div>

        {/* ナビリンク */}
        <nav className="mobile-drawer-nav">
          {nav.map((group) => (
            <div key={group.group}>
              <div className="mobile-drawer-group-label">{group.group}</div>
              {group.items.map((item) => {
                const isActive = pathname === item.to
                  || (item.to !== '/' && pathname.startsWith(item.to))
                return (
                  <button
                    key={item.to}
                    className={`mobile-drawer-item${isActive ? ' active' : ''}`}
                    onClick={() => go(item.to)}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 18,
                        minWidth: 18,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                )
              })}
            </div>
          ))}
        </nav>

        {/* フッター */}
        <div className="mobile-drawer-footer">
          <ColorModeToggleButton mobile />
          <button className="mobile-drawer-account-btn" onClick={() => go(isAdmin ? '/admin-help' : '/help')}>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <HelpCircle size={14} strokeWidth={1.8} />
            </span>{' '}
            {isAdmin ? '管理者ヘルプ' : '使い方・ヘルプ'}
          </button>
          <button className="mobile-drawer-account-btn" onClick={() => go('/account')}>
            <span>⚙</span> アカウント設定
          </button>
          <button className="mobile-drawer-logout-btn" onClick={() => { if (confirmLeave()) setShowLogoutConfirm(true) }}>
            <span>⏏</span> ログアウト
          </button>
        </div>
      </aside>
    </>
  )
}

// ── トップバー ───────────────────────────────────────────────────
function TopBar({ onHamburger, isMobile }: { onHamburger: () => void; isMobile: boolean }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, logout, isAdmin } = useAuth()

  const adminTitles: Record<string, string> = {
    '/': 'ダッシュボード', '/works': '作品・話数管理',
    '/personnel': 'アシスタント管理', '/help-staff': 'ヘルプスタッフ管理', '/reports': '日報', '/expenses': '支払管理',
    '/finance': '収入・税積立', '/monthly-report': '月次レポート',
    '/payroll-mgmt': '給与管理',
    '/fixed-expense-settings': '固定費設定',
    '/works/progress-settings': '制作進行設定',
    '/works/production-plan':   '制作計画',
    '/account': 'アカウント設定',
    '/account/security': 'ログイン・セキュリティ',
    '/account/security/admin': '認証管理',
    '/admin-help': '管理者ヘルプ',
  }
  const assistantTitles: Record<string, string> = {
    '/reports':       '日報',
    '/attendance':    '勤怠入力',
    '/shift-request': 'シフト希望',
    '/shifts':        '確定シフト',
    '/payroll':       '給与申告',
    '/payslips':      '給与明細',
    '/works':         '作品・話数',
    '/help':          '使い方・ヘルプ',
    '/account':       'アカウント設定',
    '/account/security': 'ログイン・セキュリティ',
  }
  const titles = isAdmin ? adminTitles : assistantTitles
  const homeUrl = isAdmin ? '/' : '/reports'

  return (
    <header className="topbar">
      {isMobile && (
        <button onClick={onHamburger} aria-label="メニューを開く" style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 22, color: 'var(--ink)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 44, minHeight: 44, flexShrink: 0, padding: '4px 6px',
        }}>☰</button>
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <h1 className="topbar-title"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {titles[pathname] ?? 'MangaOS'}
        </h1>
        {!isMobile && pathname !== homeUrl && pathname !== '/account' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ink-3)' }}>
            <Link to={homeUrl} style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
              onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}>ホーム</Link>
            <span>›</span>
            {pathname === '/fixed-expense-settings' ? (
              <>
                <Link to="/expenses" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
                  onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}>支払管理</Link>
                <span>›</span>
                <span>固定費設定</span>
              </>
            ) : pathname === '/works/progress-settings' ? (
              <>
                <Link to="/works" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
                  onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}>作品・話数</Link>
                <span>›</span>
                <span>制作進行設定</span>
              </>
            ) : pathname === '/works/production-plan' ? (
              <>
                <Link to="/works" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
                  onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}>作品・話数</Link>
                <span>›</span>
                <span>制作計画</span>
              </>
            ) : pathname.startsWith('/account/security') ? (
              <>
                <Link to="/account" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}
                  onClick={(e) => { if (!confirmLeave()) e.preventDefault() }}>アカウント設定</Link>
                <span>›</span>
                <span>{titles[pathname] ?? pathname}</span>
              </>
            ) : (
              <span>{titles[pathname] ?? pathname}</span>
            )}
          </div>
        )}
      </div>

      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <ThemeToggleButton />
            <ColorModeToggleButton />
          <RunnerToggleButton />

          {!isAdmin && user?.assistant_name && (
            <span style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 500 }}>
              {user.assistant_name}
            </span>
          )}
          <button className="btn btn-ghost btn-sm"
            style={{ fontSize: 11, color: 'var(--ink-3)',
              textDecoration: pathname === '/account' ? 'underline' : 'none' }}
            onClick={() => { if (confirmLeave()) navigate('/account') }}>
            {user?.email ?? ''}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--ink-3)' }}
            onClick={() => { if (confirmLeave()) logout() }}>
            ログアウト
          </button>
        </div>
      )}
    </header>
  )
}


// ── テーマ状態をApp側でも監視する ───────────────────────────────
function useThemeMode() {
  const [theme, setTheme] = useState<'work' | 'manga'>(() => {
    if (typeof window === 'undefined') return 'work'
    const saved = window.localStorage.getItem('mangaos-theme')
    return saved === 'manga' ? 'manga' : 'work'
  })

  useEffect(() => {
    const syncTheme = () => {
      const saved = window.localStorage.getItem('mangaos-theme')
      setTheme(saved === 'manga' ? 'manga' : 'work')
    }

    syncTheme()
    window.addEventListener('storage', syncTheme)
    window.addEventListener('mangaos-theme-change', syncTheme)

    return () => {
      window.removeEventListener('storage', syncTheme)
      window.removeEventListener('mangaos-theme-change', syncTheme)
    }
  }, [])

  return theme
}


// ── 下部ランナー表示/非表示状態をApp側でも監視する ───────────────
const RUNNERS_STORAGE_KEY = 'mangaos-runners-hidden'

function useRunnersHidden() {
  const [hidden, setHidden] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(RUNNERS_STORAGE_KEY) === '1'
  })

  useEffect(() => {
    const sync = () => {
      setHidden(window.localStorage.getItem(RUNNERS_STORAGE_KEY) === '1')
    }

    sync()
    window.addEventListener('storage', sync)
    window.addEventListener('mangaos-runners-change', sync)

    return () => {
      window.removeEventListener('storage', sync)
      window.removeEventListener('mangaos-runners-change', sync)
    }
  }, [])

  return hidden
}

// Manga Mode のときだけ、ダッシュボードを完全再現版に差し替える
function DashboardRoute() {
  const theme = useThemeMode()
  return theme === 'manga' ? <MangaDashboard /> : <Dashboard />
}

// ── 保護レイアウト ───────────────────────────────────────────────
function ProtectedLayout() {
  const { isAdmin, user } = useAuth()
  const isMobile    = useIsMobile()
  const runnersHidden = useRunnersHidden()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const assistantId = isAdmin ? undefined : (user as any)?.assistant_id
  useReportReminder(assistantId)

  return (
    <AuthGuard>
      {/* ドロワーはレイアウト外（position:fixed のため位置に影響しない） */}
      {isMobile && (
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}

      <div className="layout">
        {/* PC サイドバー（モバイルは CSS で非表示） */}
        <Sidebar />

        <div className="main">
          <TopBar onHamburger={() => setDrawerOpen(true)} isMobile={isMobile} />
          {/* アシスタント向け：仮メール登録案内バナー（操作を止めない） */}
          {!isAdmin && <TempEmailBanner />}
          {/* 追加認証の設定案内（未設定のときだけ。操作は止めない） */}
          <MfaNudgeBanner />
          <div className="page-content">
            <Routes>
              <Route path="/account" element={<AccountSettings />} />
              <Route path="/account/security" element={<LoginSecurity />} />
              {/* 認証管理は管理者専用（画面側の案内。実際の拒否は API がサーバーで行う） */}
              <Route path="/account/security/admin" element={<AuthManagement />} />

              {isAdmin && <>
                <Route path="/"                          element={<DashboardRoute />} />
                <Route path="/works/progress-settings"   element={<ProductionPlanningSettings />} />
                <Route path="/works/production-plan"     element={<ProductionPlan />} />
                <Route path="/works"                     element={<Works />} />
                <Route path="/personnel"                 element={<Personnel />} />
                <Route path="/help-staff"                element={<HelpStaff />} />
                <Route path="/expenses"               element={<Expenses />} />
                <Route path="/finance"                element={<Finance />} />
                <Route path="/monthly-report"         element={<MonthlyReport />} />
                <Route path="/payroll-mgmt"          element={<Payroll />} />
                <Route path="/fixed-expense-settings" element={<FixedExpenseTemplates />} />
                <Route path="/reports"                element={<Reports />} />
                <Route path="/admin-help"             element={<AdminHelp />} />
              </>}

              {!isAdmin && <>
                <Route path="/"               element={<AssistantReports />} />
                <Route path="/reports"        element={<AssistantReports />} />
                <Route path="/attendance"     element={<AssistantAttendance />} />
                <Route path="/shift-request"  element={<AssistantShiftRequest />} />
                <Route path="/shifts"         element={<AssistantShifts />} />
                <Route path="/payroll"        element={<AssistantPayrollClaim />} />
                <Route path="/payslips"       element={<AssistantPayslips />} />
                <Route path="/works"          element={<AssistantWorks />} />
                <Route path="/help"           element={<AssistantHelp />} />
              </>}
            </Routes>
          </div>
          {!runnersHidden && <RunningCharacters />}
        </div>
      </div>
    </AuthGuard>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login"               element={<Login />} />
          <Route path="/help-submit/:token"  element={<HelpSubmit />} />
          <Route path="/*"                   element={<ProtectedLayout />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}